import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, LogOut, User, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import LogoImage from "@/components/ui/logo-image";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import NotificationBell from "@/components/pwa/NotificationBell";
import { lockBodyScroll } from "@/lib/scrollLock";
import { formatPlanName } from "@/lib/formatPlanName";

/**
 * Shared sidebar/header shell for the 5 provider-type dashboards (generic
 * member Dashboard, Ambulance, BloodBank, HealthCamp, HomeCare) — previously
 * 5 near-identical hand-copied ~250-350 line files that had already drifted
 * (HomeCare resolved photo URLs via getUploadsUrl(), Ambulance/HealthCamp
 * didn't and silently showed broken avatars for relative paths; nav wrapper
 * element/animation differed). Progress Tracker D1.
 *
 * AdminLayout.tsx is deliberately NOT part of this consolidation — it has
 * role-based nav filtering across 30+ items, an embedded AdminUtilityToolbar,
 * and a genuinely different visual design system (no gradients/blur/motion).
 * Forcing it into this shell would trade a clean abstraction for an awkward
 * one; it stays a separate, purpose-built layout.
 *
 * Every Tailwind class below is a static literal (never runtime-concatenated)
 * so the build's content scanner can see and generate it — accent variants
 * are looked up by key, not string-interpolated.
 */

export type DashboardAccent = "member" | "ambulance" | "bloodbank" | "healthcamp" | "homecare";

const ACCENT_THEMES: Record<DashboardAccent, {
  pageBgTint: string;
  strong: string;
  faint5: string;
  faint10: string;
  ring: string;
  ringOffset10: string;
  hoverBg: string;
  shadow: string;
}> = {
  member: {
    pageBgTint: "to-primary/5",
    strong: "from-primary to-healing-green",
    faint5: "from-primary/5 to-healing-green/5",
    faint10: "from-primary/10 to-cyan-500/10",
    ring: "ring-primary/30",
    ringOffset10: "ring-primary/20",
    hoverBg: "hover:bg-primary/10",
    shadow: "shadow-primary/30",
  },
  ambulance: {
    pageBgTint: "to-primary/5",
    strong: "from-primary to-cyan-500",
    faint5: "from-primary/5 to-cyan-500/5",
    faint10: "from-primary/10 to-cyan-500/10",
    ring: "ring-primary/30",
    ringOffset10: "ring-primary/20",
    hoverBg: "hover:bg-primary/10",
    shadow: "shadow-primary/30",
  },
  bloodbank: {
    pageBgTint: "to-primary/5",
    strong: "from-primary to-rose-500",
    faint5: "from-primary/5 to-rose-500/5",
    faint10: "from-primary/10 to-rose-500/10",
    ring: "ring-primary/30",
    ringOffset10: "ring-primary/20",
    hoverBg: "hover:bg-primary/10",
    shadow: "shadow-primary/30",
  },
  healthcamp: {
    pageBgTint: "to-primary/5",
    strong: "from-primary to-cyan-500",
    faint5: "from-primary/5 to-cyan-500/5",
    faint10: "from-primary/10 to-cyan-500/10",
    ring: "ring-primary/30",
    ringOffset10: "ring-primary/20",
    hoverBg: "hover:bg-primary/10",
    shadow: "shadow-primary/30",
  },
  homecare: {
    pageBgTint: "to-teal-500/5",
    strong: "from-teal-500 to-cyan-500",
    faint5: "from-teal-500/5 to-cyan-500/5",
    faint10: "from-teal-500/10 to-cyan-500/10",
    ring: "ring-teal-500/30",
    ringOffset10: "ring-teal-500/20",
    hoverBg: "hover:bg-teal-500/10",
    shadow: "shadow-teal-500/30",
  },
};

export interface DashboardMenuItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  path: string;
}

export interface DashboardShellProps {
  children: React.ReactNode;
  accent: DashboardAccent;
  menuItems: DashboardMenuItem[];
  headerIcon: React.ComponentType<{ className?: string }>;
  headerTitle: string;

  displayName: string;
  subtitle?: string;
  photoUrl?: string;
  /** 3-state badge when set; falls back to the simple isVerified badge otherwise. */
  kycStatus?: "approved" | "pending" | "rejected" | null;
  isVerified?: boolean;
  subscriptionPlan?: string;

  dashboardType: string;
  entityId: string | number;
  notificationsPath: string;

  profilePath?: string;
  settingsPath: string;
  onLogout: () => void;
}

const DashboardShell = ({
  children,
  accent,
  menuItems,
  headerIcon: HeaderIcon,
  headerTitle,
  displayName,
  subtitle,
  photoUrl,
  kycStatus,
  isVerified,
  subscriptionPlan,
  dashboardType,
  entityId,
  notificationsPath,
  profilePath,
  settingsPath,
  onLogout,
}: DashboardShellProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logoUrl: siteLogo } = useSiteSettings();
  const theme = ACCENT_THEMES[accent];

  const avatarInitial = displayName.charAt(0).toUpperCase();

  // Mobile sidebar is a drawer over the page, not a route change — without this
  // the background page keeps scrolling underneath it while it's open.
  useEffect(() => {
    if (!isSidebarOpen) return;
    return lockBodyScroll();
  }, [isSidebarOpen]);

  const handleLogout = () => {
    onLogout();
    navigate("/");
  };

  const renderVerifiedBadge = () => {
    if (kycStatus) {
      switch (kycStatus) {
        case "approved":
          return <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs px-2 py-0.5 border-0">✓ Verified</Badge>;
        case "pending":
          return <Badge className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-xs px-2 py-0.5 border-0">⏳ Pending</Badge>;
        case "rejected":
          return <Badge className="bg-gradient-to-r from-red-500 to-rose-500 text-white text-xs px-2 py-0.5 border-0">✗ Rejected</Badge>;
        default:
          return null;
      }
    }
    if (isVerified) {
      return <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs px-2 py-0.5 border-0">✓ Verified</Badge>;
    }
    return null;
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-background via-background ${theme.pageBgTint}`}>
      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-72 bg-white/90 dark:bg-card/90 backdrop-blur-xl border-r border-white/20 shadow-2xl transform transition-transform duration-300 lg:translate-x-0 flex flex-col ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Sidebar Header */}
        <div className={`h-16 flex items-center justify-between px-4 border-b border-white/20 bg-gradient-to-r ${theme.faint5} shrink-0`}>
          <Link to="/" className="flex items-center gap-2">
            <LogoImage logoUrl={siteLogo} alt="Patakare" className="h-10 w-auto object-contain drop-shadow-md" />
          </Link>
          <button
            className={`lg:hidden p-2 ${theme.hoverBg} rounded-xl transition-colors`}
            onClick={() => setIsSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable content: identity card + nav + subscription badge */}
        <div className="flex-1 overflow-y-auto sidebar-scrollbar">
          {/* Identity Card */}
          <div className="p-4 border-b border-white/20">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex items-center gap-3 p-3 bg-gradient-to-r ${theme.faint10} rounded-2xl border border-primary/20`}
            >
              <Avatar className={`h-12 w-12 ring-2 ${theme.ring} ring-offset-2 shrink-0`}>
                <AvatarImage src={photoUrl} />
                <AvatarFallback className={`bg-gradient-to-br ${theme.strong} text-white font-bold`}>
                  {avatarInitial}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{displayName}</p>
                <div className="flex flex-wrap items-center gap-1 mt-1">
                  {subtitle && (
                    <Badge variant="secondary" className="text-xs px-2 py-0.5 bg-primary/10 text-primary border-primary/20 capitalize">
                      {subtitle}
                    </Badge>
                  )}
                  {renderVerifiedBadge()}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-2">
            {menuItems.map((item, index) => {
              const isActive = location.pathname === item.path;
              return (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                >
                  <Link to={item.path} onClick={() => setIsSidebarOpen(false)}>
                    <motion.div
                      whileHover={{ x: 4 }}
                      whileTap={{ scale: 0.98 }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        isActive
                          ? `bg-gradient-to-r ${theme.strong} text-white shadow-lg ${theme.shadow}`
                          : `text-muted-foreground ${theme.hoverBg} hover:text-foreground`
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                      {isActive && <div className="ml-auto w-2 h-2 rounded-full bg-white" />}
                    </motion.div>
                  </Link>
                </motion.div>
              );
            })}
          </nav>

          {/* Subscription Badge */}
          {subscriptionPlan && (
            <div className="mx-4 mb-4 p-3 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded-xl border border-amber-500/20">
              <p className="text-xs text-muted-foreground">Current Plan</p>
              <p className="font-semibold text-amber-600 truncate">{formatPlanName(subscriptionPlan)}</p>
            </div>
          )}
        </div>

        {/* Logout Button - Fixed at bottom. paddingBottom adds env(safe-area-inset-bottom)
            on top of the base p-4 so the button clears the iOS home indicator in an
            installed PWA instead of sitting flush against it. */}
        <div
          className="shrink-0 p-4 bg-white/95 dark:bg-card/95 backdrop-blur-xl border-t border-white/20"
          style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
        >
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              variant="outline"
              className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive hover:border-destructive hover:bg-destructive/5 rounded-xl h-12"
              onClick={handleLogout}
            >
              <LogOut className="w-5 h-5" />
              Logout
            </Button>
          </motion.div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-72">
        {/* Top Header */}
        <motion.header
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="sticky top-0 z-30 h-16 bg-white/80 dark:bg-card/80 backdrop-blur-xl border-b border-white/20 flex items-center justify-between px-4 lg:px-6 shadow-sm"
          // Same iOS-PWA safe-area fix as Header.tsx — installed standalone
          // mode has no browser chrome, so without this the hamburger button
          // (and this whole bar) sits partly under the status bar/notch and
          // taps there get swallowed by iOS instead of reaching the page.
          style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(4rem + env(safe-area-inset-top))' }}
        >
          <div className="flex items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className={`lg:hidden p-2 ${theme.hoverBg} rounded-xl transition-colors`}
              onClick={() => setIsSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </motion.button>
            <div className="hidden sm:flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${theme.strong} flex items-center justify-center`}>
                <HeaderIcon className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-lg font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                {headerTitle}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <NotificationBell
              dashboardType={dashboardType}
              entityId={entityId}
              onViewAll={() => navigate(notificationsPath)}
              onNotificationClick={(notif) => navigate(notif.action_url || notificationsPath)}
            />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <motion.div whileHover={{ scale: 1.02 }}>
                  <Button variant="ghost" className={`gap-2 ${theme.hoverBg} rounded-xl px-3`}>
                    <Avatar className={`h-8 w-8 ring-2 ${theme.ringOffset10}`}>
                      <AvatarImage src={photoUrl} />
                      <AvatarFallback className={`bg-gradient-to-br ${theme.strong} text-white text-sm font-bold`}>
                        {avatarInitial}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline-block font-medium truncate max-w-[120px]">
                      {displayName}
                    </span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </motion.div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white/95 dark:bg-card/95 backdrop-blur-xl border-white/20 rounded-xl p-2">
                {profilePath && (
                  <DropdownMenuItem asChild className="rounded-lg">
                    <Link to={profilePath} className="cursor-pointer">
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild className="rounded-lg">
                  <Link to={settingsPath} className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-border/50" />
                <DropdownMenuItem
                  className="text-destructive cursor-pointer rounded-lg"
                  onClick={handleLogout}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </motion.header>

        {/* Page Content */}
        <main className="p-4 lg:p-6 relative">{children}</main>
      </div>
    </div>
  );
};

export default DashboardShell;